import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Send, Users, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/* AdminPush — Phase A round 3
 *
 * Lets Coach Lewis send a Web Push broadcast to all opted-in subscribers
 * (or filtered by audience), and review the recent send history.
 *
 * Built deliberately minimal: one composer form on the left, history list
 * on the right. No scheduled sends yet — that's a Phase B feature.
 */

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
};

const audienceOptions = [
  { value: 'all', label: 'All subscribers' },
  { value: 'logged_in', label: 'Logged-in only' },
  { value: 'anonymous', label: 'Anonymous only' },
];

export default function AdminPush() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/app/login');
  const [audience, setAudience] = useState('all');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [subscribers, setSubscribers] = useState({ active: 0, logged_in: 0, anonymous: 0 });
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/push/broadcasts?limit=20`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setHistory(data.broadcasts || []);
      setSubscribers(data.subscribers || { active: 0, logged_in: 0, anonymous: 0 });
    } catch (err) {
      logger.warn('AdminPush history fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    const trimTitle = title.trim();
    const trimBody = body.trim();
    if (trimTitle.length < 2) { toast.error('Title is required (2+ chars).'); return; }
    if (trimBody.length < 2) { toast.error('Message body is required (2+ chars).'); return; }
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/push/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: trimTitle,
          body: trimBody,
          url: url.trim() || '/',
          audience,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(
        `Sent ${data.sent || 0} of ${data.matched || 0} (${data.failed || 0} failed).`,
      );
      // Reset composer for next send.
      setTitle('');
      setBody('');
      await loadHistory();
    } catch (err) {
      logger.warn('AdminPush send failed', err);
      toast.error('Broadcast failed. Check backend logs.');
    } finally {
      setSending(false);
    }
  }, [title, body, url, audience, sending, loadHistory]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5" data-testid="admin-push-page">
      {/* Composer — left 3 columns */}
      <Card className="lg:col-span-3" data-testid="admin-push-composer">
        <CardHeader className="border-b border-foreground/10">
          <CardTitle className="text-[16px] font-heading flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#004C54] text-white inline-flex items-center justify-center">
              <Bell className="h-4 w-4" />
            </div>
            Compose push broadcast
          </CardTitle>
          <p className="text-[12.5px] text-foreground/65 leading-snug mt-1">
            Sends a Web Push to every opted-in browser in the selected audience.
            Coach Lewis voice: short, useful, no hype. Max 80 chars title, 300 body.
          </p>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="push-title" className="text-[12.5px] font-semibold">
              Title <span className="text-foreground/45">({title.length}/80)</span>
            </Label>
            <Input
              id="push-title"
              value={title}
              maxLength={80}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Pre-game cue · 2 hours out"
              data-testid="admin-push-title-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="push-body" className="text-[12.5px] font-semibold">
              Body <span className="text-foreground/45">({body.length}/300)</span>
            </Label>
            <Textarea
              id="push-body"
              value={body}
              maxLength={300}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Three breaths. Then we lock in. Find a quiet spot — 60 seconds is enough."
              data-testid="admin-push-body-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="push-url" className="text-[12.5px] font-semibold">
              Click destination
            </Label>
            <Input
              id="push-url"
              value={url}
              maxLength={300}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/app/login or /quiz"
              data-testid="admin-push-url-input"
            />
            <p className="text-[11px] text-foreground/55">
              Relative path inside Apex Mind. Leave as <code>/app/login</code> to land
              users on their dashboard.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[12.5px] font-semibold">Audience</Label>
            <div className="grid grid-cols-3 gap-2">
              {audienceOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAudience(opt.value)}
                  className={
                    'px-3 py-2 rounded-lg text-[12px] font-semibold border transition-colors ' +
                    (audience === opt.value
                      ? 'border-[#004C54] bg-[#004C54]/8 text-[#004C54]'
                      : 'border-foreground/10 text-foreground/55 hover:border-foreground/30')
                  }
                  data-testid={`admin-push-audience-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Live preview */}
          <div
            className="rounded-xl border border-foreground/10 bg-[#0B0C0D] text-white p-4"
            data-testid="admin-push-preview"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono-accent mb-2">
              Preview
            </div>
            <div className="flex items-start gap-3">
              <div className="shrink-0 h-9 w-9 rounded-lg bg-white/10 inline-flex items-center justify-center">
                <Bell className="h-4 w-4 text-white/80" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold leading-tight truncate">
                  {title || 'Pre-game cue · 2 hours out'}
                </div>
                <div className="text-[12px] text-white/70 leading-snug mt-1 line-clamp-3">
                  {body || 'Three breaths. Then we lock in. Find a quiet spot — 60 seconds is enough.'}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end pt-2">
            <Button
              type="button"
              onClick={submit}
              disabled={sending || !title.trim() || !body.trim()}
              className="h-10 px-5 rounded-full bg-[#004C54] hover:bg-[#003a40] text-white font-semibold"
              data-testid="admin-push-send"
            >
              {sending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  Sending
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5 mr-2" />
                  Send broadcast
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats + history — right 2 columns */}
      <div className="lg:col-span-2 space-y-5">
        <Card data-testid="admin-push-stats">
          <CardHeader className="border-b border-foreground/10">
            <CardTitle className="text-[15px] font-heading flex items-center gap-2">
              <Users className="h-4 w-4 text-[#004C54]" />
              Subscribers
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-3">
            {[
              { label: 'Active total', value: subscribers.active, key: 'active' },
              { label: 'Logged-in users', value: subscribers.logged_in, key: 'logged_in' },
              { label: 'Anonymous', value: subscribers.anonymous, key: 'anonymous' },
            ].map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between"
                data-testid={`admin-push-stat-${row.key}`}
              >
                <span className="text-[12.5px] text-foreground/65">{row.label}</span>
                <span className="font-heading font-bold text-[18px] text-foreground tabular-nums">
                  {row.value}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="admin-push-history">
          <CardHeader className="border-b border-foreground/10">
            <CardTitle className="text-[15px] font-heading">Recent broadcasts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-5 text-[12.5px] text-foreground/50 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading history…
              </div>
            ) : history.length === 0 ? (
              <div className="p-5 text-[12.5px] text-foreground/55 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 text-foreground/40 mt-0.5 shrink-0" />
                No broadcasts sent yet. Your first one will appear here.
              </div>
            ) : (
              <ul className="divide-y divide-foreground/8">
                {history.map((b) => (
                  <li
                    key={b.id}
                    className="px-5 py-3 hover:bg-secondary/40 transition-colors"
                    data-testid={`admin-push-history-row-${b.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-semibold text-foreground truncate">
                          {b.title}
                        </div>
                        <div className="text-[11.5px] text-foreground/55 truncate mt-0.5">
                          {b.body}
                        </div>
                        <div className="text-[10.5px] text-foreground/40 mt-1.5">
                          {fmtDate(b.created_at)} &middot; {b.audience}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10.5px] text-foreground/40 uppercase tracking-wide">
                          Sent
                        </div>
                        <div className="font-heading font-bold text-[14px] text-foreground tabular-nums">
                          {b.sent}/{b.matched}
                        </div>
                        {b.failed > 0 && (
                          <div className="text-[10px] text-red-500 mt-0.5">
                            {b.failed} failed
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card data-testid="admin-push-coach-tip">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0 h-9 w-9 rounded-lg bg-[#004C54]/8 text-[#004C54] inline-flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[12.5px] font-bold text-foreground">Coach Lewis voice</div>
                <p className="text-[11.5px] text-foreground/65 mt-1 leading-snug">
                  Keep it short. One actionable cue per send. No hype, no exclamation marks. If the
                  athlete reads it in 3 seconds, you nailed it.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
